import { ArenaGameState } from '../../../state/ArenaTypes';
import {
  GameEventItem,
  IGameEventSynthesizer,
  GameEventSynthesizerFactory
} from './types';

/**
 * Base abstract synthesizer providing reusable transition detection
 * and lifecycle management across all card games.
 *
 * Contains ZERO game-specific rules. Game domain logic is implemented
 * strictly in concrete game event synthesizers (e.g. HeartsEventSynthesizer).
 */
export abstract class GameEventSynthesizer implements IGameEventSynthesizer {
  abstract readonly gameType: string;

  /**
   * Tracks hand numbers where an event has already triggered to enforce once-per-hand semantics.
   */
  protected triggeredHands = new Map<string, Set<number>>();

  /**
   * Detects whether a domain flag transitioned from false -> true during active play
   * strictly once per hand.
   */
  protected detectOncePerHandTransition(
    eventKey: string,
    isTriggered: boolean,
    prevTriggered: boolean,
    handNumber: number,
    phase?: string
  ): boolean {
    if (phase !== 'PLAYING') return false;
    if (!isTriggered || prevTriggered) return false;

    let handSet = this.triggeredHands.get(eventKey);
    if (!handSet) {
      handSet = new Set<number>();
      this.triggeredHands.set(eventKey, handSet);
    }

    if (handSet.has(handNumber)) {
      return false;
    }

    handSet.add(handNumber);
    return true;
  }

  /**
   * Resets hand-scoped state for a specific hand number (e.g. on new deal).
   */
  public resetHand(handNumber: number): void {
    this.triggeredHands.forEach((set) => set.delete(handNumber));
  }

  /**
   * Resets all internal tracking state.
   */
  public reset(): void {
    this.triggeredHands.clear();
  }

  abstract synthesize(
    currentState: ArenaGameState,
    previousState?: ArenaGameState | null
  ): GameEventItem[];
}

/**
 * Creates a stable deterministic event ID incorporating game type, event type, and hand number.
 */
export function createDeterministicEventId(
  gameType: string,
  eventType: string,
  handNumber: number | string
): string {
  return `${gameType.toLowerCase()}_${eventType.toLowerCase()}_hand_${handNumber}`;
}

/**
 * Registry for GameEventSynthesizers.
 * Supports registering factories and instances by canonical gameType and aliases.
 */
export class GameEventSynthesizerRegistry {
  private static factories = new Map<string, GameEventSynthesizerFactory>();
  private static instances = new Map<string, IGameEventSynthesizer>();

  public static register(
    gameType: string,
    synthesizerOrFactory: IGameEventSynthesizer | GameEventSynthesizerFactory,
    aliases?: readonly string[]
  ): void {
    const primaryKey = gameType.trim().toUpperCase();
    const factory =
      typeof synthesizerOrFactory === 'function'
        ? (synthesizerOrFactory as GameEventSynthesizerFactory)
        : () => synthesizerOrFactory;

    this.factories.set(primaryKey, factory);
    if (typeof synthesizerOrFactory !== 'function') {
      this.instances.set(primaryKey, synthesizerOrFactory);
    }

    if (aliases) {
      for (const alias of aliases) {
        const aliasKey = alias.trim().toUpperCase();
        this.factories.set(aliasKey, factory);
        if (typeof synthesizerOrFactory !== 'function') {
          this.instances.set(aliasKey, synthesizerOrFactory);
        }
      }
    }
  }

  public static get(gameType?: string | null): IGameEventSynthesizer | undefined {
    if (!gameType || !gameType.trim()) return undefined;
    const key = gameType.trim().toUpperCase();
    if (this.instances.has(key)) {
      return this.instances.get(key);
    }
    const factory = this.factories.get(key);
    if (factory) {
      const instance = factory();
      this.instances.set(key, instance);
      return instance;
    }
    return undefined;
  }

  public static create(gameType?: string | null): IGameEventSynthesizer | undefined {
    if (!gameType || !gameType.trim()) return undefined;
    const key = gameType.trim().toUpperCase();
    const factory = this.factories.get(key);
    if (factory) {
      return factory();
    }
    return this.instances.get(key);
  }

  public static getAll(): IGameEventSynthesizer[] {
    return Array.from(this.instances.values());
  }

  public static clear(): void {
    this.factories.clear();
    this.instances.clear();
  }
}

/**
 * Resolves a fresh event synthesizer instance for the specified game state.
 * Checks cardGameType and gameType against the synthesizer registry.
 */
export function resolveEventSynthesizer(gameState?: ArenaGameState | null): IGameEventSynthesizer | undefined {
  if (!gameState) return undefined;
  const gameTypeKey = gameState.cardGameType || gameState.gameType;
  if (!gameTypeKey) return undefined;
  return GameEventSynthesizerRegistry.create(String(gameTypeKey));
}
